import React, { useState, useRef } from "react";
import {
  X,
  Send,
  Paperclip,
  Trash2,
  AlertCircle,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import {
  Ticket,
  TicketStatus,
  useRespondToTicket,
} from "../../../lib/api/tickets";

interface TicketResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  onResponseSent: () => void;
}

const TicketResponseModal: React.FC<TicketResponseModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onResponseSent,
}) => {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<TicketStatus | "">("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: sendResponse, isPending } = useRespondToTicket();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter((file) => {
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "application/pdf",
          "text/plain",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        return file.size <= maxSize && allowedTypes.includes(file.type);
      });

      setAttachments((prev) => [...prev, ...validFiles].slice(0, 3));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!message.trim()) {
      setError("Please enter a response message");
      return;
    }

    if (message.trim().length < 10) {
      setError("Response must be at least 10 characters");
      return;
    }

    if (!ticket) return;

    sendResponse(
      {
        ticketId: ticket._id,
        message: message.trim(),
        status: status || undefined,
        attachments,
      },
      {
        onSuccess: () => {
          setMessage("");
          setStatus("");
          setAttachments([]);
          onResponseSent();
        },
        onError: () => {
          setError("Failed to send response. Please try again.");
        },
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      setMessage("");
      setStatus("");
      setAttachments([]);
      setError("");
      onClose();
    }
  };

  if (!isOpen || !ticket) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Send Response</h2>
            <p className="text-sm text-gray-500">
              Responding to{" "}
              <span className="text-[#8B52FF] font-mono">
                {ticket.ticketNumber}
              </span>
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 space-y-5"
        >
          {/* Ticket Info Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-500 mb-1">Subject</p>
            <p className="font-medium text-gray-900">{ticket.subject}</p>
            <p className="text-sm text-gray-500 mt-2 mb-1">User Email</p>
            <p className="text-gray-700">{ticket.email}</p>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Response Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your response to the user..."
              rows={6}
              className={`w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all resize-none ${
                error ? "border-red-300" : "border-gray-200"
              }`}
              disabled={isPending}
            />
            <div className="flex items-center justify-between mt-1">
              {error ? (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {error}
                </p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400">
                {message.length} characters
              </span>
            </div>
          </div>

          {/* Status Update */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Update Status{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: "", label: "No Change", icon: null },
                { value: "in_progress", label: "In Progress", icon: Clock },
                { value: "resolved", label: "Resolved", icon: CheckCircle },
                { value: "closed", label: "Closed", icon: XCircle },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatus(option.value as TicketStatus | "")}
                  disabled={isPending}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                    status === option.value
                      ? "border-[#8B52FF] bg-[#8B52FF]/10 text-[#8B52FF]"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  } disabled:opacity-50`}
                >
                  {option.icon && <option.icon className="w-3.5 h-3.5" />}
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments{" "}
              <span className="font-normal text-gray-400">
                (optional, max 3)
              </span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.doc,.docx"
              disabled={isPending || attachments.length >= 3}
            />

            {attachments.length < 3 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}
                className="w-full border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#8B52FF]/50 transition-colors disabled:opacity-50"
              >
                <Paperclip className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <span className="text-sm text-gray-500">
                  Click to attach files
                </span>
              </button>
            )}

            {attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
                  >
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className="flex-1 text-sm text-gray-700 truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      disabled={isPending}
                      className="p-1 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Your response will be sent to{" "}
              <span className="font-medium">{ticket.email}</span> via email. The
              user will receive a notification with your message.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 flex items-center gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            className="flex-1 px-6 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !message.trim()}
            className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Response
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketResponseModal;
